import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation } from '@/js/libs/router';
import { Loader, Typography } from '@/js/libs/ui';
import { Icons } from '@/js/libs/ui/src/V2';

import { Card, IconWrapper, Page } from './styles';

const REDIRECT_DELAY_MS = 3000;
const REDIR_PARAM_PATTERN = /[?&]redir=(.*)$/;

const safeDecode = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

const toHttpUrl = (value: string): string | null => {
  try {
    const protocol = new URL(value).protocol;
    if (protocol === 'http:' || protocol === 'https:') {
      return value;
    }
  } catch {
    return null;
  }
  return null;
};

const parseRedirectUrl = (locationSearchAndHash: string): string | null => {
  const rawValue = REDIR_PARAM_PATTERN.exec(locationSearchAndHash)?.[1];
  if (rawValue === undefined) {
    return null;
  }

  const decodedValue = safeDecode(rawValue);

  return toHttpUrl(rawValue) ?? (decodedValue ? toHttpUrl(decodedValue) : null);
};

export const ClickPreview = memo(function ClickPreview() {
  const [t] = useTranslation(['creatives']);
  const { search, hash } = useLocation();
  const redirectUrl = useMemo(() => parseRedirectUrl(`${search}${hash}`), [search, hash]);

  useEffect(() => {
    if (!redirectUrl) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      window.location.href = redirectUrl;
    }, REDIRECT_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [redirectUrl]);

  if (!redirectUrl) {
    return (
      <Page>
        <Card>
          <IconWrapper $variant='warning'>
            <Icons.Warning filled size={64} color='currentcolor' />
          </IconWrapper>
          <Typography.Title>{t('creatives:CLICK_TRACKING_NO_REDIRECT')}</Typography.Title>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <IconWrapper $variant='success'>
          <Icons.Check filled size={64} color='currentcolor' />
        </IconWrapper>
        <Typography.Title>{t('creatives:CLICK_TRACKING_SUCCESSFUL')}</Typography.Title>
        <Typography.TextMedium>{t('creatives:CLICK_TRACKING_REDIRECTING')}</Typography.TextMedium>
        <Typography.TextMedium>{new URL(redirectUrl).host}</Typography.TextMedium>
        <Loader />
      </Card>
    </Page>
  );
});
